import {
  keepTruthy
} from "./chunk-YDAKBU6Q.js";
import {
  lastWriteWins
} from "./chunk-4SQQRBMJ.js";
import {
  anyOf,
  applogsSummary,
  dateNowIso,
  ensureTsPvAndFinalizeApplog,
  getLogsFromThread,
  rollingFilter
} from "./chunk-236RMLMX.js";
import {
  areCidsEqual,
  encodeBlockOriginal,
  prepareForPub
} from "./chunk-KPMINROC.js";

// src/pubsub/snap-push.ts
import * as dagJson2 from "@ipld/dag-json";
import { Logger as Logger2 } from "besonders-logger";
import stringify from "safe-stable-stringify";

// src/ipfs/car.ts
import { CarReader, CarWriter } from "@ipld/car";
import * as dagJson from "@ipld/dag-json";
import { Logger } from "besonders-logger";
import { CID } from "multiformats";
var { WARN, LOG, DEBUG, VERBOSE, ERROR } = Logger.setup(Logger.INFO);
async function decodePubFromCar(car) {
  const decoded = await getBlocksOfCar(car);
  return await decodePubFromBlocks(decoded);
}
async function decodePubFromBlocks({ rootCID, blockStore }, _recursionTrace = [], stopAtCID) {
  if (!rootCID || !blockStore) {
    throw ERROR("Empty roots/blocks", { rootCID, blockStore });
  }
  let allApplogs = [];
  let firstInfo = null;
  let currentCID = rootCID;
  const visited = /* @__PURE__ */ new Set();
  let applogsCID = null;
  while (currentCID) {
    const cidStr = currentCID.toString();
    if (visited.has(cidStr)) {
      throw ERROR("[decodePubFromBlocks] pub chain has a loop", {
        currentCID: cidStr,
        visited: [...visited]
      });
    }
    visited.add(cidStr);
    const root = await getDecodedBlock(blockStore, currentCID);
    VERBOSE(`[decodePubFromBlocks] root:`, cidStr, root, { blockStore });
    if (!root) {
      throw ERROR("[decodePubFromBlocks] root not found in blockStore", { blockStore, currentCID: cidStr });
    }
    let pubLogsArray;
    if (root?.info) {
      if (!applogsCID) applogsCID = root.applogs;
      const applogsBlock = await getDecodedBlock(blockStore, root.applogs);
      if (!applogsBlock) {
        throw ERROR("[decodePubFromBlocks] applogs block missing from blockStore", {
          cid: root.applogs.toString(),
          rootCID: cidStr
        });
      }
      pubLogsArray = await unchunkApplogsBlock(applogsBlock, blockStore);
      if (!firstInfo) {
        const decoded = await getDecodedBlock(blockStore, root.info);
        if (decoded) {
          firstInfo = decoded;
          DEBUG(`new format - infoLogs`, firstInfo.logs.map((l) => ({ [l.toString()]: l })));
        } else {
          WARN(`[decodePubFromBlocks] info block not found for ${root.info}, using empty info`);
          firstInfo = { logs: [] };
        }
      }
    } else {
      pubLogsArray = root.applogs;
    }
    const resolveLogFromCidLink = async (cidOrLink) => {
      const cid = cidOrLink;
      const applog = await getDecodedBlock(blockStore, cid);
      if (!applog) {
        ERROR(`Could not find applog CID in pub blocks:`, cid.toString(), { cid, root, blockStore });
        throw new Error(`Could not find applog CID in pub blocks: ${cid.toString()}`);
      }
      const pvCid = CID.asCID(applog.pv);
      if (pvCid) applog.pv = pvCid.toV1().toString();
      return {
        ...applog,
        cid: cid.toV1().toString()
      };
    };
    const snapshotApplogs = await Promise.all(pubLogsArray.map(resolveLogFromCidLink));
    allApplogs = allApplogs.concat(snapshotApplogs);
    if (!root.prev) {
      break;
    }
    if (stopAtCID && areCidsEqual(root.prev, stopAtCID)) {
      DEBUG("[decodePubFromBlocks] stopping at stopAtCID:", stopAtCID.toString());
      break;
    }
    const prevBytes = await blockStore.get(root.prev);
    if (!prevBytes) {
      throw ERROR("[decodePubFromBlocks] prev snapshot missing from blockStore", {
        currentCID: cidStr,
        prev: root.prev.toString(),
        stopAtCID: stopAtCID?.toString(),
        visited: [...visited]
      });
    }
    currentCID = root.prev;
  }
  const result = {
    cid: rootCID,
    info: firstInfo ? {
      ...firstInfo,
      logs: await Promise.all(firstInfo.logs.map(async (cidOrLink) => {
        const cid = cidOrLink;
        const applog = await getDecodedBlock(blockStore, cid);
        if (!applog) {
          ERROR(`Could not find info log CID in pub blocks:`, cid.toString(), { cid, blockStore });
          throw new Error(`Could not find info log CID in pub blocks: ${cid.toString()}`);
        }
        const pvCid = CID.asCID(applog.pv);
        if (pvCid) applog.pv = pvCid.toV1().toString();
        return {
          ...applog,
          cid: cid.toV1().toString()
        };
      }))
    } : null,
    applogsCID,
    applogs: allApplogs
  };
  DEBUG("[decodePubFromBlocks] result:", result, { rootCID: rootCID.toString(), blockStore, applogs: allApplogs });
  return result;
}
async function getBlocksOfCar(car) {
  const rootsFromCar = await car.getRoots();
  const roots = rootsFromCar.map((c) => (typeof c.toV1 === "function" ? c : CID.decode(c.bytes)).toV1().toString());
  const blocks = /* @__PURE__ */ new Map();
  for await (const { cid: cidFromCarblocks, bytes } of car.blocks()) {
    const cid = typeof cidFromCarblocks.toV1 === "function" ? cidFromCarblocks : CID.decode(cidFromCarblocks.bytes);
    VERBOSE({ cidFromCarblocks, cid });
    blocks.set(cid.toV1().toString(), bytes);
  }
  if (roots.length !== 1) {
    WARN("Unexpected roots count:", roots);
  }
  return {
    rootCID: CID.parse(roots[0]),
    blockStore: {
      get: (cid) => blocks.get(cid.toV1().toString())
    }
  };
}
async function getDecodedBlock(blockStore, cid) {
  try {
    var blob = await blockStore.get(cid);
    if (!blob) {
      WARN("returning null");
      return null;
    }
  } catch (err) {
    if (err.message === "Not Found") return null;
    throw err;
  }
  return dagJson.decode(blob);
}
async function makeCarOut(roots, blocks) {
  const { writer, out } = CarWriter.create(Array.isArray(roots) ? roots : [roots]);
  VERBOSE(`Writing ${blocks.length} blocks to CAR`, { roots, blocks });
  blocks.forEach((b) => writer.put(b));
  writer.close();
  return out;
}
async function makeCarBlob(roots, blocks) {
  const carOut = await makeCarOut(roots, blocks);
  const chunks = [];
  for await (const chunk of carOut) {
    chunks.push(chunk);
  }
  const blob = new Blob(chunks);
  return blob;
}
async function carFromBlob(blob) {
  return CarReader.fromBytes(new Uint8Array(await blob.arrayBuffer()));
}
function extractCids(value) {
  const cid = CID.asCID(value);
  if (cid) return [cid];
  if (Array.isArray(value)) return value.flatMap(extractCids);
  if (value && typeof value === "object") return Object.values(value).flatMap(extractCids);
  return [];
}
var MAX_COLLECT_BLOCKS = 1e6;
async function collectDagBlocks(startCID, blockStore) {
  const visited = /* @__PURE__ */ new Set();
  const blocks = [];
  const queue = [startCID];
  while (queue.length > 0) {
    if (blocks.length >= MAX_COLLECT_BLOCKS) {
      WARN(`[collectDagBlocks] hit ${MAX_COLLECT_BLOCKS} block limit, returning partial result`);
      break;
    }
    const cid = queue.shift();
    const cidStr = cid.toString();
    if (visited.has(cidStr)) continue;
    visited.add(cidStr);
    let bytes;
    try {
      bytes = await blockStore.get(cid);
    } catch {
      WARN(`[collectDagBlocks] block not found: ${cidStr}, stopping this branch`);
      continue;
    }
    if (!bytes) {
      WARN(`[collectDagBlocks] block not found: ${cidStr}, stopping this branch`);
      continue;
    }
    blocks.push({ cid, bytes });
    if (blocks.length % 1e3 === 0) {
      LOG(`[collectDagBlocks] collected ${blocks.length} blocks...`);
    }
    try {
      const decoded = dagJson.decode(bytes);
      const childCids = extractCids(decoded);
      for (const child of childCids) {
        if (!visited.has(child.toString())) {
          queue.push(child);
        }
      }
    } catch {
    }
  }
  DEBUG(`[collectDagBlocks] collected ${blocks.length} blocks from ${startCID.toString()}`);
  return blocks;
}
function streamReaderToIterable(bodyReader) {
  return (async function* () {
    while (true) {
      const { done, value } = await bodyReader.read();
      VERBOSE(`[car] chunk`, { done, value });
      if (done) {
        break;
      }
      yield value;
    }
  })();
}

// src/pubsub/snap-push.ts
var { WARN: WARN2, LOG: LOG2, DEBUG: DEBUG2, VERBOSE: VERBOSE2, ERROR: ERROR2 } = Logger2.setup(Logger2.INFO);
async function prepareSnapshotForPush(agent, appThread, threadToPublish, share, prevSnapCID, prevCounter) {
  if (prevCounter !== null && !prevSnapCID) {
    throw ERROR2("[prepareSnapshotForPush] prevCounter provided without prevSnapCID");
  }
  let logsToPublish = getLogsFromThread(threadToPublish);
  DEBUG2(`[preparePubForPush] Collected ${logsToPublish.length} logs :`, {
    logsToPublish,
    threadOrLogsCount: threadToPublish.nameAndSizeUntracked || `[${threadToPublish.length}]`
  });
  const { sharedAgents, sharedKeyMap, sharedKey, pubCounter } = share ?? {};
  const getExistingOrNewLog = (thread, share2, ag, at, vl) => {
    let logInQuestion = rollingFilter(lastWriteWins(thread), { en: share2.id, at }).latestLog;
    if (!logInQuestion && vl !== void 0) {
      logInQuestion = ensureTsPvAndFinalizeApplog({ ag, en: share2.id, at, vl }, thread);
    }
    return logInQuestion;
  };
  const shareNameLog = getExistingOrNewLog(appThread, share, agent.ag, "share/name", share.name);
  const shareCounterLog = getExistingOrNewLog(appThread, share, agent.ag, "share/counter", `${agent.did}<::>${pubCounter}`);
  const encryptApplog = async (applog, keyToUse) => {
    const { log: eachLog, cid } = prepareForPub(applog);
    const enc = new TextEncoder();
    const stringified = stringify(eachLog);
    const stringifiedEncodedAppLogPayload = enc.encode(stringified);
    VERBOSE2("[odd]", { eachLog, stringified, stringifiedEncodedAppLogPayload });
    try {
      const encPayload = await agent.crypto?.aes.encrypt(stringifiedEncodedAppLogPayload, keyToUse, "AES-GCM");
      VERBOSE2("[odd] encrypted length:", stringifiedEncodedAppLogPayload.length, { encPayload });
      return encPayload;
    } catch (err) {
      throw ERROR2("FAILED TO ENC payload length:", stringifiedEncodedAppLogPayload.length, { err });
    }
  };
  let maybeEncryptedApplogs;
  const encryptedApplogs = [];
  const agentSharedKeyLogs = [];
  if (sharedAgents) {
    if (!sharedKey || !sharedKeyMap) {
      throw ERROR2("sharedAgents but no Keys/Map", { sharedAgents, sharedKeyMap, sharedKey });
    }
    VERBOSE2("encrypting", { sharedAgents, sharedKeyMap });
    for (const [eachAgent, eachEncKey] of Array.from(sharedKeyMap.entries())) {
      VERBOSE2("adding key", { eachAgent, eachEncKey });
      agentSharedKeyLogs.push({
        ag: agent.ag,
        en: eachAgent,
        at: "share/sharedKey",
        vl: eachEncKey
        // these are encrypted with the derived key from the local agent private and remote agent public keys
      });
    }
    const CIDlist = [];
    const pubCIDmap = {};
    for (const eachLog of logsToPublish) {
      VERBOSE2("[crypto] encrypting ", { eachLog, sharedKey });
      const encPayload = await encryptApplog(eachLog, sharedKey);
      DEBUG2("[crypto] encrypted ", { eachLog, encPayload, sharedKey });
      encryptedApplogs.push({ enc: encPayload });
    }
    maybeEncryptedApplogs = encryptedApplogs;
  } else {
    maybeEncryptedApplogs = logsToPublish;
  }
  DEBUG2("adding all agent info and shareAtoms", {
    share,
    agent,
    logsToPublish,
    // threadToPublish, - very verbose
    agentSharedKeyLogs
  });
  const infoLogs = [
    ...rollingFilter(lastWriteWins(appThread), {
      // TODO: use static filter for performance
      en: agent.ag,
      at: anyOf("agent/ecdh", "agent/jwkd", "agent/appAgent")
    }).applogs,
    ...shareNameLog ? [shareNameLog] : [],
    ...shareCounterLog ? [shareCounterLog] : [],
    ...agentSharedKeyLogs,
    { ag: agent.ag, en: share.id, at: "share/publishTs", vl: dateNowIso() }
  ];
  DEBUG2(`[prepareSnapshotForPush] info logs:`, infoLogs);
  if (!infoLogs.find(({ at }) => at === "agent/appAgent")) throw ERROR2(`[prepareSnapshotForPush] appThread missing agent/appAgent log`);
  const applogsToEncode = keepTruthy(maybeEncryptedApplogs);
  const infologsToEncode = keepTruthy(infoLogs);
  if (!applogsToEncode.length) {
    throw ERROR2("no valid applogs", {
      agent: agent.did,
      prevSnapCID: prevSnapCID?.toString(),
      counts: {
        rawApplogs: maybeEncryptedApplogs.length,
        keptApplogs: applogsToEncode.length,
        rawInfo: infoLogs.length,
        keptInfo: infologsToEncode.length
      },
      rawApplogsSample: applogsSummary(maybeEncryptedApplogs)
    });
  }
  if (!infologsToEncode.length) {
    throw ERROR2("no valid infologs", {
      agent: agent.did,
      prevSnapCID: prevSnapCID?.toString(),
      counts: {
        rawApplogs: maybeEncryptedApplogs.length,
        keptApplogs: applogsToEncode.length,
        rawInfo: infoLogs.length,
        keptInfo: infologsToEncode.length
      },
      rawInfoSample: applogsSummary(infoLogs)
    });
  }
  const encodedSnapshot = await encodeSnapshotAsCar(agent, applogsToEncode, infologsToEncode, prevSnapCID, prevCounter);
  DEBUG2("inPrepareSnapshotForPush", { encodedSnapshot });
  return encodedSnapshot;
}
async function encodeSnapshotAsCar(agent, applogs, infoLogs, prevSnapCID, prevCounter) {
  DEBUG2(`[encodeSnapshotAsCar] encoding`, { agent, applogs, infoLogs });
  const { cids: infoLogCids, encodedApplogs: encodedInfoLogs } = await encodeApplogsAsIPLD(infoLogs);
  const { cids: applogCids, encodedApplogs } = await encodeApplogsAsIPLD(applogs);
  let blocks = encodedApplogs.concat(encodedInfoLogs);
  const infoLogsWrap = await encodeBlockOriginal({ logs: infoLogCids });
  blocks.push(infoLogsWrap);
  const { rootCID: chunkRootCID, blocks: chunkBlocks } = await chunkApplogs(applogCids);
  blocks = blocks.concat(chunkBlocks);
  const infoSignature = await agent.sign(infoLogsWrap.cid.bytes);
  const applogsSignature = await agent.sign(chunkRootCID.bytes);
  const root = {
    info: infoLogsWrap.cid,
    applogs: chunkRootCID,
    infoSignature,
    applogsSignature,
    prev: prevSnapCID,
    prevCounter: !prevSnapCID ? 0 : prevCounter !== null ? prevCounter + 1 : null
  };
  DEBUG2("[encodeSnapshotAsCar] encoding root", { root, logCids: applogCids, infoLogCids });
  const encodedRoot = await encodeBlockOriginal(root);
  blocks.push(encodedRoot);
  DEBUG2("[encodeSnapshotAsCar] => root", { encodedRoot });
  return {
    cid: encodedRoot.cid,
    blob: await makeCarBlob(encodedRoot.cid, blocks),
    // TODO: create CarBuilder (incl .encodeAndAdd({...}))
    blocks,
    infoLogCids,
    applogCids
  };
}
async function chunkApplogs(applogCids, size = 1e4) {
  if (!applogCids.length) throw ERROR2(`[chunkApplogs] called with empty array`);
  const chunks = [];
  for (let i = 0; i < applogCids.length; i += size) {
    const chunk = await encodeBlockOriginal({ logs: applogCids.slice(i, Math.min(i + applogCids.length, i + size)) });
    chunks.push(chunk);
  }
  if (chunks.length === 1) return { rootCID: chunks[0].cid, blocks: chunks };
  const root = await encodeBlockOriginal({ chunks: chunks.map((chunk) => chunk.cid) });
  const blocks = [root, ...chunks];
  DEBUG2(`[chunkApplogs] ${applogCids.length} logs chunked into ${chunks.length}`, { applogCids, root, blocks, chunks, dagJson: dagJson2 });
  return { rootCID: root.cid, blocks, chunks };
}
async function unchunkApplogsBlock(block, blockStore) {
  if (!block) return [];
  if (isSnapBlockChunks(block)) {
    return (await Promise.all(
      block.chunks.map(async (chunkCid) => {
        const block2 = await getDecodedBlock(blockStore, chunkCid);
        if (!block2?.logs) throw ERROR2(`Weird chunk`, block2);
        return block2.logs;
      })
    )).flat();
  } else {
    return block.logs ?? [];
  }
}
function isSnapBlockChunks(block) {
  return !!block && "chunks" in block;
}
async function encodeSnapshotApplogsAsCar(applogs) {
  const encoded = await encodeApplogsAsIPLD(applogs);
  if (!encoded) throw ERROR2("invalid applogs cannot continue", { encoded, applogs: applogsSummary(applogs) });
  const { cids, encodedApplogs } = encoded;
  const root = { applogs: cids };
  const encodedRoot = await encodeBlockOriginal(root);
  DEBUG2("[encodeSnapshotApplogsAsCar] encoded root", { cids, encodedRoot });
  return await makeCarBlob(encodedRoot.cid, [encodedRoot, ...encodedApplogs]);
}
async function encodeApplogsAsIPLD(applogs) {
  DEBUG2({ applogs });
  const validApplogs = applogs.filter((eachLog) => !!eachLog);
  DEBUG2({ validApplogs });
  if (!validApplogs.length) throw ERROR2("no valid applogs");
  const preppedLogs = validApplogs.map((log) => prepareForPub(log).log);
  const encodedApplogs = await Promise.all(preppedLogs.map(encodeBlockOriginal));
  DEBUG2("[encodeApplogsAsIpld] encoded applogs", { preppedLogs, encodedApplogs });
  const cids = encodedApplogs.map((b) => {
    if (!b.cid) throw ERROR2(`[publish] no cid for encoded log:`, b);
    return b.cid;
  });
  return { cids, encodedApplogs };
}

export {
  prepareSnapshotForPush,
  encodeSnapshotAsCar,
  chunkApplogs,
  unchunkApplogsBlock,
  isSnapBlockChunks,
  encodeSnapshotApplogsAsCar,
  decodePubFromCar,
  decodePubFromBlocks,
  getBlocksOfCar,
  getDecodedBlock,
  makeCarOut,
  makeCarBlob,
  carFromBlob,
  collectDagBlocks,
  streamReaderToIterable
};
//# sourceMappingURL=chunk-EYY3KCRO.js.map